import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Badge, Button, Card, Container, Spinner } from 'react-bootstrap'
import { BsArrowLeft, BsArrowClockwise, BsClock, BsGeoAlt, BsSpeedometer2 } from 'react-icons/bs'
import { FaMapMarkerAlt } from 'react-icons/fa'
import toast from 'react-hot-toast'
import ApiEndpoints from '../commonComponents/ApiEndpoints'
import {
  computeEtaSeconds,
  computeSpeedKmh,
  formatDistanceKm,
  formatEtaSeconds,
  formatSpeedKmh,
  haversineDistanceKm,
  readGeolocationOnce,
  sameCoords,
  useBusJourneyTracking,
} from './useBusJourneyTracking'
import { useAuth } from '../AuthContext'
import { useSidebar } from '../SidebarContext'
import { isBusAttendantUser } from '../../utils/busAttendant'
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from '@react-google-maps/api'

// ─── Google Maps API Key ──────────────────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = 'AIzaSyCrrGSwzDZG1UW_FgWZnFFAUtHYLtzhCk4'

// IMPORTANT: define outside component to prevent reloading the script on every render
const GMAPS_LIBRARIES = ['places']

const LATEST_LOCATION_POLL_MS = 30 * 1000

function dash(v) {
  return v === null || v === undefined || String(v).trim() === '' ? '—' : String(v)
}

function normalizeBusJourneyStatus(bus) {
  const st = bus?.bus_journey_status ?? bus?.busJourneyStatus ?? null
  if (st == null || st === '') return null
  return String(st).toLowerCase()
}

function formatRelativeAgo(ms) {
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 48) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}

// ─── Google Bus Map Component ─────────────────────────────────────────────────
function GoogleBusMap({
  mapCenter,
  mapPoints,
  busNumber,
  locationText,
  hasUserCoords,
  userCoords,
  approxDistanceText,
   onAddressResolved,
}) {
  const mapRef = useRef(null)
  const [busInfoOpen, setBusInfoOpen] = useState(false)
  const [userInfoOpen, setUserInfoOpen] = useState(false)
  const [busAddress, setBusAddress] = useState(null)

  const busPosition = { lat: mapCenter[0], lng: mapCenter[1] }
  const userPosition = hasUserCoords
    ? { lat: Number(userCoords.lat), lng: Number(userCoords.long) }
    : null

  // Reverse geocode bus position to get a human-readable address
  useEffect(() => {
    if (!window.google || !mapCenter) return
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: busPosition }, (results, status) => {
      console.log('Status:', status)
      if (status === 'OK' && results?.[0]) {
        // setBusAddress(results[0].formatted_address)
        onAddressResolved(results[0].formatted_address)
      }
    })
  }, [mapCenter[0], mapCenter[1]])

  // On map load: fit bounds if we have both markers
  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map
      const valid = (mapPoints || []).filter(
        (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]),
      )
      if (valid.length >= 2) {
        const bounds = new window.google.maps.LatLngBounds()
        valid.forEach(([lat, lng]) => bounds.extend({ lat, lng }))
        map.fitBounds(bounds)
      }
    },
    [mapPoints],
  )

  // Re-fit bounds when mapPoints update
  useEffect(() => {
    if (!mapRef.current) return
    const valid = (mapPoints || []).filter(
      (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]),
    )
    if (valid.length >= 2) {
      const bounds = new window.google.maps.LatLngBounds()
      valid.forEach(([lat, lng]) => bounds.extend({ lat, lng }))
      mapRef.current.fitBounds(bounds)
    } else {
      mapRef.current.setCenter(busPosition)
      mapRef.current.setZoom(16)
    }
  }, [JSON.stringify(mapPoints)])

  return (
    <GoogleMap
      mapContainerStyle={{ height: '100%', width: '100%' }}
      center={busPosition}
      zoom={16}
      onLoad={handleMapLoad}
      options={{
        gestureHandling: 'cooperative',
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      }}
    >
      {/* Bus Marker */}
      <Marker
        position={busPosition}
        title={`Bus ${dash(busNumber)}`}
        onClick={() => setBusInfoOpen((v) => !v)}
      />
      {busInfoOpen && (
        <InfoWindow
          position={busPosition}
          onCloseClick={() => setBusInfoOpen(false)}
          options={{ maxWidth: 220 }}
        >
          <div style={{ maxWidth: 220, fontFamily: 'inherit' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
              Bus {dash(busNumber)}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
              {busAddress || locationText}
            </div>
          </div>
        </InfoWindow>
      )}

      {/* User Location Marker — blue circle */}
      {hasUserCoords && userPosition && (
        <Marker
          position={userPosition}
          title="Your location"
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
          onClick={() => setUserInfoOpen((v) => !v)}
        />
      )}
      {hasUserCoords && userPosition && userInfoOpen && (
        <InfoWindow
          position={userPosition}
          onCloseClick={() => setUserInfoOpen(false)}
          options={{ maxWidth: 220 }}
        >
          <div style={{ maxWidth: 220, fontFamily: 'inherit' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>Your location</div>
            {approxDistanceText ? (
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {approxDistanceText} from bus (approx.)
              </div>
            ) : null}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  )
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const vehicleTrackMapCss = `
@media (min-width: 768px) {
  .vehicle-track-map-card {
    margin-left: auto;
    margin-right: auto;
  }
}
.vehicle-track-stats-card {
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
  color: #fff;
  border-radius: 12px;
}
.vehicle-track-stats-hero {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
}
.vehicle-track-stats-hero-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.72);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.vehicle-track-stat-tile {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 12px;
  min-height: 88px;
}
.vehicle-track-stat-tile-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.14);
  margin-bottom: 8px;
  font-size: 14px;
}
.vehicle-track-stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.68);
  margin-bottom: 4px;
}
.vehicle-track-stat-value {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;
}
.vehicle-track-stat-value-sm {
  font-size: 14px;
}
.vehicle-track-stats-note {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
}
.vehicle-track-location-banner {
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 13px;
}
`

// ─── TrackingStatsPanel ───────────────────────────────────────────────────────
function TrackingStatsPanel({
  distanceText, speedText, etaText, isStopped, lastMs,
  locationLoading, userLocationLoading, userLocationError,
  hasUserCoords, hasCoords, onRetryLocation,
}) {
  const approxDistance = distanceText ? `~${distanceText}` : null
  const heroValue =
    etaText || approxDistance ||
    (hasCoords && locationLoading ? 'Updating…' : hasCoords ? '—' : 'Waiting for bus')
  const heroLabel = etaText
    ? 'Approx. time to reach you'
    : distanceText ? 'Approx. distance from you'
    : hasCoords ? 'Live tracking' : 'Bus location'

  const speedValue = speedText || (hasCoords && hasUserCoords ? 'Calculating…' : '—')
  const distanceValue = approxDistance || (userLocationLoading ? 'Locating you…' : '—')
  const reachTimeValue = etaText || (hasUserCoords && hasCoords && speedText ? 'Calculating…' : '—')

  return (
    <Card className="border-0 shadow-sm mb-3 vehicle-track-stats-card">
      <Card.Body className="p-3 p-md-4">
        <div className="mb-3">
          <div className="vehicle-track-stats-hero-label">{heroLabel}</div>
          <div className="vehicle-track-stats-hero">{heroValue}</div>
          {etaText && approxDistance ? (
            <div className="vehicle-track-stats-note mt-1">
              Based on {approxDistance} away at {speedText || 'current speed'} (approx.)
            </div>
          ) : null}
          {isStopped && etaText ? (
            <div className="vehicle-track-stats-note mt-1">Bus stopped in traffic — arrival time held</div>
          ) : null}
        </div>
        <div className="row g-2 g-md-3">
          <div className="col-4">
            <div className="vehicle-track-stat-tile h-100">
              <div className="vehicle-track-stat-tile-icon"><BsGeoAlt /></div>
              <div className="vehicle-track-stat-label">Approx. distance</div>
              <div className="vehicle-track-stat-value">{distanceValue}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="vehicle-track-stat-tile h-100">
              <div className="vehicle-track-stat-tile-icon"><BsSpeedometer2 /></div>
              <div className="vehicle-track-stat-label">Bus speed</div>
              <div
                className={`vehicle-track-stat-value${speedText && speedText.length > 14 ? ' vehicle-track-stat-value-sm' : ''}`}
                style={isStopped ? { color: '#fbbf24' } : undefined}
              >{speedValue}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="vehicle-track-stat-tile h-100">
              <div className="vehicle-track-stat-tile-icon"><BsClock /></div>
              <div className="vehicle-track-stat-label">Time to reach you</div>
              <div className="vehicle-track-stat-value vehicle-track-stat-value-sm">{reachTimeValue}</div>
            </div>
          </div>
        </div>
        {lastMs != null ? (
          <div className="vehicle-track-stats-note mt-2">Last bus update: {formatRelativeAgo(lastMs)}</div>
        ) : null}
        {!hasUserCoords && hasCoords ? (
          <div className="vehicle-track-location-banner mt-3">
            {userLocationLoading ? 'Getting your location to show distance and arrival time…'
              : userLocationError ? (
                <>{userLocationError}{' '}
                  <Button variant="link" className="p-0 align-baseline"
                    style={{ fontSize: 13, color: '#c2410c' }} onClick={onRetryLocation}>
                    Try again
                  </Button>
                </>
              ) : 'Allow location access to see how far the bus is from you.'}
          </div>
        ) : null}
        {hasUserCoords && hasCoords && !etaText && !userLocationLoading ? (
          <div className="vehicle-track-stats-note mt-3">
            Time to reach you will appear once bus speed is calculated from the next location update.
          </div>
        ) : null}
      </Card.Body>
    </Card>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function VehicleTrackPage() {
  const [busAddress, setBusAddress] = useState('')
  const navigate = useNavigate()
  const { vehicle_id } = useParams()
  const { user } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setMarginLeft(isCollapsed ? '81px' : '261px')
      } else {
        setMarginLeft('0px')
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const pageStyle = {
    marginLeft,
    transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    padding: 12,
  }

  const busId = useMemo(() => {
    const n = Number(vehicle_id)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [vehicle_id])

  const [bus, setBus] = useState(null)
  const [busLoading, setBusLoading] = useState(true)
  const [attendantName, setAttendantName] = useState('—')
  const [journeyActive, setJourneyActive] = useState(false)
  const [locationLoading, setLocationLoading] = useState(false)
  const [userCoords, setUserCoords] = useState(null)
  const [userLocationLoading, setUserLocationLoading] = useState(false)
  const [userLocationError, setUserLocationError] = useState(null)
  const [busMotion, setBusMotion] = useState({ speedKmh: null, etaSeconds: null, isStopped: false })
  const lastRefreshMsRef = useRef(0)
  const busMotionRef = useRef({ prevCoords: null, prevFetchedMs: null, lastEtaSeconds: null, lastSpeedKmh: null })

  const { latestByBusId, fetchLatest, startOrUpdateLocation, completeJourney, inFlight } =
    useBusJourneyTracking()

  const canManageJourney = useMemo(() => {
    if (!busId || !isBusAttendantUser(user)) return false
    const uid = Number(user?.id)
    const aid = Number(bus?.bus_attendant_id)
    return Number.isFinite(uid) && Number.isFinite(aid) && uid === aid
  }, [busId, user, bus?.bus_attendant_id])

  const loadBusAndAttendant = useCallback(async () => {
    if (!busId) return
    setBusLoading(true)
    try {
      const res = await ApiEndpoints.getBusById(busId)
      const raw = res?.data?.data ?? res?.data ?? null
      setBus(raw)
      setJourneyActive(normalizeBusJourneyStatus(raw) === 'started')
      const attendantId = raw?.bus_attendant_id
      if (attendantId == null || attendantId === '') { setAttendantName('—'); return }
      if (!isBusAttendantUser(user)) { setAttendantName(`User #${attendantId}`); return }
      try {
        const ares = await ApiEndpoints.getBusAttendants()
        const list = ares.data?.data ?? ares.data ?? []
        const row = Array.isArray(list) ? list.find((x) => Number(x?.id ?? x?.user_id) === Number(attendantId)) : null
        setAttendantName(
          row?.name || [row?.first_name, row?.last_name].filter(Boolean).join(' ') ||
          row?.email || `User #${attendantId}` || '—'
        )
      } catch { setAttendantName(`User #${attendantId}`) }
    } catch (error) {
      setBus(null)
      toast.error(error?.response?.data?.message || error?.message || 'Could not load vehicle details.')
    } finally { setBusLoading(false) }
  }, [busId])

  const refreshLocation = useCallback(async () => {
    if (!busId) return
    setLocationLoading(true)
    try { await fetchLatest(busId) } finally { setLocationLoading(false) }
  }, [busId, fetchLatest])

  useEffect(() => { loadBusAndAttendant() }, [loadBusAndAttendant])
  useEffect(() => { if (!busId) return; refreshLocation() }, [busId, refreshLocation])

  const loadUserLocation = useCallback(async () => {
    setUserLocationLoading(true)
    setUserLocationError(null)
    try {
      const position = await readGeolocationOnce()
      setUserCoords(position)
    } catch (error) {
      setUserCoords(null)
      setUserLocationError(
        error?.code === 1
          ? 'Location permission denied. Allow location access to see distance from you.'
          : error?.message || 'Could not read your location.',
      )
    } finally { setUserLocationLoading(false) }
  }, [])

  useEffect(() => { loadUserLocation() }, [loadUserLocation])

  useEffect(() => {
    if (!busId) return
    let cancelled = false
    const poll = window.setInterval(() => {
      if (!cancelled) { lastRefreshMsRef.current = Date.now(); fetchLatest(busId) }
    }, LATEST_LOCATION_POLL_MS)
    return () => { cancelled = true; window.clearInterval(poll) }
  }, [busId, fetchLatest])

  useEffect(() => {
    busMotionRef.current = { prevCoords: null, prevFetchedMs: null, lastEtaSeconds: null, lastSpeedKmh: null }
    setBusMotion({ speedKmh: null, etaSeconds: null, isStopped: false })
  }, [busId])

  const handleStartOrUpdate = useCallback(async () => {
    if (!busId) return
    try { await startOrUpdateLocation(busId); setJourneyActive(true); await loadBusAndAttendant() } catch {}
  }, [busId, startOrUpdateLocation, loadBusAndAttendant])

  const handleComplete = useCallback(async () => {
    if (!busId) return
    try { await completeJourney(busId); setJourneyActive(false); await loadBusAndAttendant() } catch {}
  }, [busId, completeJourney, loadBusAndAttendant])

  const latest = busId != null ? latestByBusId[Number(busId)] : null
  const coords = latest?.coords ?? null
  const hasCoords = coords != null && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.long))
  const mapCenter = hasCoords ? [Number(coords.lat), Number(coords.long)] : null
  const lastMs = latest?.updatedMs ?? null
  const locationText = hasCoords
    ? `${Number(coords.lat).toFixed(6)}°, ${Number(coords.long).toFixed(6)}°`
    : latest?.noLocationMessage || '—'
  const hasUserCoords = userCoords != null && Number.isFinite(Number(userCoords.lat)) && Number.isFinite(Number(userCoords.long))
  const distanceKm = hasCoords && hasUserCoords
    ? haversineDistanceKm(Number(coords.lat), Number(coords.long), Number(userCoords.lat), Number(userCoords.long))
    : null
  const distanceText = distanceKm != null ? formatDistanceKm(distanceKm) : userLocationError ? '—' : null
  const approxDistanceText = distanceText && distanceText !== '—' ? `~${distanceText}` : null

  useEffect(() => {
    if (!hasCoords || latest?.lastFetchedMs == null) return
    const current = { lat: Number(coords.lat), long: Number(coords.long) }
    const fetchedMs = latest.lastFetchedMs
    const motion = busMotionRef.current
    if (motion.prevFetchedMs === fetchedMs) return
    if (motion.prevCoords == null || motion.prevFetchedMs == null) {
      busMotionRef.current = { ...motion, prevCoords: current, prevFetchedMs: fetchedMs }
      return
    }
    const isStopped = sameCoords(motion.prevCoords, current)
    let speedKmh = motion.lastSpeedKmh
    let etaSeconds = motion.lastEtaSeconds
    if (isStopped) {
      speedKmh = 0
    } else {
      const elapsedMs = fetchedMs - motion.prevFetchedMs
      const nextSpeed = computeSpeedKmh(motion.prevCoords.lat, motion.prevCoords.long, current.lat, current.long, elapsedMs)
      if (nextSpeed != null && Number.isFinite(nextSpeed)) { speedKmh = nextSpeed; motion.lastSpeedKmh = nextSpeed }
      if (distanceKm != null && speedKmh != null && speedKmh > 0) { etaSeconds = computeEtaSeconds(distanceKm, speedKmh); motion.lastEtaSeconds = etaSeconds }
    }
    busMotionRef.current = { ...motion, prevCoords: current, prevFetchedMs: fetchedMs, lastEtaSeconds: etaSeconds, lastSpeedKmh: isStopped ? motion.lastSpeedKmh : speedKmh }
    setBusMotion({ speedKmh: speedKmh ?? null, etaSeconds: etaSeconds ?? null, isStopped })
  }, [coords?.lat, coords?.long, distanceKm, hasCoords, latest?.lastFetchedMs])

  const speedText = busMotion.speedKmh != null
    ? busMotion.isStopped ? 'Stopped (traffic)' : formatSpeedKmh(busMotion.speedKmh)
    : null

  const etaSeconds = useMemo(() => {
    if (distanceKm == null || !hasUserCoords) return null
    if (busMotion.isStopped) return busMotion.etaSeconds
    if (busMotion.speedKmh != null && busMotion.speedKmh > 0) return computeEtaSeconds(distanceKm, busMotion.speedKmh)
    return busMotion.etaSeconds
  }, [busMotion.etaSeconds, busMotion.isStopped, busMotion.speedKmh, distanceKm, hasUserCoords])

  const etaText = etaSeconds != null ? `${formatEtaSeconds(etaSeconds)} (approx.)` : null

  const mapPoints = []
  if (hasCoords) mapPoints.push([Number(coords.lat), Number(coords.long)])
  if (hasUserCoords) mapPoints.push([Number(userCoords.lat), Number(userCoords.long)])
  const busNumber = bus?.bus_number ?? bus?.busNumber ?? busId

  if (busId == null) {
    return (
      <div className="page-shell-mobile-offset" style={pageStyle}>
        <Container className="py-4">
          <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <div className="fw-bold" style={{ fontSize: 16 }}>Invalid vehicle id</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>Please open this page with a valid vehicle id.</div>
                </div>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>Go back</Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-shell-mobile-offset" style={pageStyle}>
      <Container fluid className="p-0">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
          <Button variant="outline-secondary" className="d-inline-flex align-items-center gap-2" onClick={() => navigate(-1)}>
            <BsArrowLeft /> Back
          </Button>
          <Button variant="outline-primary" className="d-inline-flex align-items-center gap-2"
            disabled={busLoading} onClick={() => { loadBusAndAttendant(); refreshLocation() }}>
            <BsArrowClockwise /> Refresh
          </Button>
        </div>

        {busLoading ? (
          <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4 d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span className="text-muted">Loading vehicle…</span>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
              <Card.Body className="p-3 p-md-4">
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                  <div className="min-w-0">
                    <div className="fw-bold" style={{ fontSize: 16, color: '#0f172a' }}>{dash(busNumber)}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>Attendant: {dash(attendantName)}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={journeyActive ? 'success' : hasCoords ? 'info' : 'secondary'} pill>
                      {journeyActive ? 'Journey live' : hasCoords ? 'Last known' : 'No position'}
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <style>{vehicleTrackMapCss}</style>
            <TrackingStatsPanel
              distanceText={distanceText} speedText={speedText} etaText={etaText}
              isStopped={busMotion.isStopped} lastMs={lastMs} locationLoading={locationLoading}
              userLocationLoading={userLocationLoading} userLocationError={userLocationError}
              hasUserCoords={hasUserCoords} hasCoords={hasCoords} onRetryLocation={loadUserLocation}
            />

            {/* ── Google Map Card ── */}
            <Card className="border-0 shadow-sm mb-3 vehicle-track-map-card" style={{ borderRadius: 12 }}>
              <div
                className="vehicle-track-map-inner"
                style={{ height: 340, background: '#e2e8f0', borderRadius: 12, overflow: 'hidden' }}
              >
                {mapCenter ? (
                  <LoadScript
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                    libraries={GMAPS_LIBRARIES}
                    loadingElement={
                      <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="text-muted small">Loading map…</span>
                      </div>
                    }
                  >
                    <GoogleBusMap
                      mapCenter={mapCenter}
                      mapPoints={mapPoints}
                      busNumber={busNumber}
                      locationText={locationText}
                      hasUserCoords={hasUserCoords}
                      userCoords={userCoords}
                      approxDistanceText={approxDistanceText}
                      onAddressResolved={setBusAddress}
                    />
                  </LoadScript>
                ) : (
                  <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                    <div className="text-muted small">{latest?.noLocationMessage || 'No position yet'}</div>
                  </div>
                )}
              </div>
            </Card>

            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
              <Card.Body className="p-3 p-md-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div
  style={{
    flex: 1,
    textAlign: 'center',
  }}
>
                    
                    
<div className="text-muted" style={{ fontSize: 12 }}>Last known location</div>
<div className="fw-semibold" style={{
    fontSize: 14,
    color: '#0f172a',
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
  }}>
  {/* {locationLoading ? 'Loading…' : hasCoords ? 'Location available on map' : '—'} */}
  {locationLoading
  ? 'Loading…'
  : busAddress || (hasCoords ? 'Location available on map' : '—')}
</div>
{lastMs != null ? (
  <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
    Updated: {formatRelativeAgo(lastMs)}
  </div>
) : null}
                    {latest?.error ? (
                      <div className="text-danger" style={{ fontSize: 12, marginTop: 6 }}>{latest.error}</div>
                    ) : null}
                  </div>
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <Button variant="outline-primary" className="d-inline-flex align-items-center gap-2"
                      disabled={!busId || Boolean(inFlight?.[Number(busId)])} onClick={() => refreshLocation()}>
                      <BsArrowClockwise /> Refresh
                    </Button>
                    {canManageJourney ? (
                      <>
                        <Button variant="success" className="d-inline-flex align-items-center gap-2 fw-semibold"
                          disabled={!busId || inFlight?.[Number(busId)] === 'start'} onClick={handleStartOrUpdate}>
                          <FaMapMarkerAlt />
                          {journeyActive ? 'Send current location' : 'Start journey (GPS)'}
                        </Button>
                        <Button variant="danger" className="fw-semibold"
                          disabled={!busId || !journeyActive || inFlight?.[Number(busId)] === 'complete'}
                          onClick={handleComplete}>
                          Complete journey
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              </Card.Body>
            </Card>

            <div className="text-muted" style={{ fontSize: 12, paddingLeft: 2 }}>
              Auto-refreshing latest location every 30 seconds.
            </div>
          </>
        )}
      </Container>
    </div>
  )
}



// import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// import { useNavigate, useParams } from 'react-router-dom'
// import { Badge, Button, Card, Container, Spinner } from 'react-bootstrap'
// import { BsArrowLeft, BsArrowClockwise, BsClock, BsGeoAlt, BsSpeedometer2 } from 'react-icons/bs'
// import { FaMapMarkerAlt } from 'react-icons/fa'
// import toast from 'react-hot-toast'
// import ApiEndpoints from '../commonComponents/ApiEndpoints'
// import {
//   computeEtaSeconds,
//   computeSpeedKmh,
//   formatDistanceKm,
//   formatEtaSeconds,
//   formatSpeedKmh,
//   haversineDistanceKm,
//   readGeolocationOnce,
//   sameCoords,
//   useBusJourneyTracking,
// } from './useBusJourneyTracking'
// import { useAuth } from '../AuthContext'
// import { useSidebar } from '../SidebarContext'
// import { isBusAttendantUser } from '../../utils/busAttendant'
// import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet'
// import L from 'leaflet'
// import 'leaflet/dist/leaflet.css'
// import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
// import markerIcon from 'leaflet/dist/images/marker-icon.png'
// import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// const LATEST_LOCATION_POLL_MS = 30 * 1000

// function dash(v) {
//   return v === null || v === undefined || String(v).trim() === '' ? '—' : String(v)
// }

// // Fix default Leaflet marker icons for bundlers (CRA/Webpack).
// delete L.Icon.Default.prototype._getIconUrl
// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: markerIcon2x,
//   iconUrl: markerIcon,
//   shadowUrl: markerShadow,
// })

// function normalizeBusJourneyStatus(bus) {
//   const st = bus?.bus_journey_status ?? bus?.busJourneyStatus ?? null
//   if (st == null || st === '') return null
//   return String(st).toLowerCase()
// }

// function formatRelativeAgo(ms) {
//   const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000))
//   if (sec < 60) return `${sec}s ago`
//   const min = Math.floor(sec / 60)
//   if (min < 60) return `${min}m ago`
//   const hr = Math.floor(min / 60)
//   if (hr < 48) return `${hr}h ago`
//   const day = Math.floor(hr / 24)
//   return `${day}d ago`
// }

// function RecenterMap({ center, zoom }) {
//   const map = useMap()
//   useEffect(() => {
//     if (!center) return
//     map.setView(center, zoom, { animate: true })
//   }, [center, map, zoom])
//   return null
// }

// function FitMapBounds({ points, fallbackCenter, fallbackZoom }) {
//   const map = useMap()
//   useEffect(() => {
//     const valid = (points || []).filter(
//       (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]),
//     )
//     if (valid.length >= 2) {
//       map.fitBounds(L.latLngBounds(valid), { padding: [48, 48], maxZoom: 16, animate: true })
//       return
//     }
//     if (valid.length === 1) {
//       map.setView(valid[0], fallbackZoom, { animate: true })
//       return
//     }
//     if (fallbackCenter) {
//       map.setView(fallbackCenter, fallbackZoom, { animate: true })
//     }
//   }, [points, fallbackCenter, fallbackZoom, map])
//   return null
// }

// const userLocationIcon = L.divIcon({
//   className: '',
//   html: '<div style="width:14px;height:14px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>',
//   iconSize: [14, 14],
//   iconAnchor: [7, 7],
// })

// const vehicleTrackMapCss = `
// @media (min-width: 768px) {
//   .vehicle-track-map-card {
//     margin-left: auto;
//     margin-right: auto;
//   }
// }
// .vehicle-track-map-inner .leaflet-top.leaflet-left {
//   left: 12px;
//   top: 12px;
// }
// .vehicle-track-stats-card {
//   background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
//   color: #fff;
//   border-radius: 12px;
// }
// .vehicle-track-stats-hero {
//   font-size: 28px;
//   font-weight: 700;
//   line-height: 1.15;
//   letter-spacing: -0.02em;
// }
// .vehicle-track-stats-hero-label {
//   font-size: 12px;
//   font-weight: 500;
//   color: rgba(255, 255, 255, 0.72);
//   text-transform: uppercase;
//   letter-spacing: 0.04em;
// }
// .vehicle-track-stat-tile {
//   background: rgba(255, 255, 255, 0.1);
//   border: 1px solid rgba(255, 255, 255, 0.12);
//   border-radius: 10px;
//   padding: 12px;
//   min-height: 88px;
// }
// .vehicle-track-stat-tile-icon {
//   width: 28px;
//   height: 28px;
//   border-radius: 8px;
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   background: rgba(255, 255, 255, 0.14);
//   margin-bottom: 8px;
//   font-size: 14px;
// }
// .vehicle-track-stat-label {
//   font-size: 11px;
//   color: rgba(255, 255, 255, 0.68);
//   margin-bottom: 4px;
// }
// .vehicle-track-stat-value {
//   font-size: 16px;
//   font-weight: 600;
//   line-height: 1.25;
//   word-break: break-word;
// }
// .vehicle-track-stat-value-sm {
//   font-size: 14px;
// }
// .vehicle-track-stats-note {
//   font-size: 12px;
//   color: rgba(255, 255, 255, 0.72);
// }
// .vehicle-track-location-banner {
//   border-radius: 10px;
//   padding: 12px 14px;
//   background: #fff7ed;
//   border: 1px solid #fed7aa;
//   color: #9a3412;
//   font-size: 13px;
// }
// `

// function TrackingStatsPanel({
//   distanceText,
//   speedText,
//   etaText,
//   isStopped,
//   lastMs,
//   locationLoading,
//   userLocationLoading,
//   userLocationError,
//   hasUserCoords,
//   hasCoords,
//   onRetryLocation,
// }) {
//   const approxDistance = distanceText ? `~${distanceText}` : null
//   const heroValue =
//     etaText ||
//     approxDistance ||
//     (hasCoords && locationLoading ? 'Updating…' : hasCoords ? '—' : 'Waiting for bus')
//   const heroLabel = etaText
//     ? 'Approx. time to reach you'
//     : distanceText
//       ? 'Approx. distance from you'
//       : hasCoords
//         ? 'Live tracking'
//         : 'Bus location'

//   const speedValue = speedText || (hasCoords && hasUserCoords ? 'Calculating…' : '—')
//   const distanceValue = approxDistance || (userLocationLoading ? 'Locating you…' : '—')
//   const reachTimeValue =
//     etaText || (hasUserCoords && hasCoords && speedText ? 'Calculating…' : '—')

//   return (
//     <Card className="border-0 shadow-sm mb-3 vehicle-track-stats-card">
//       <Card.Body className="p-3 p-md-4">
//         <div className="mb-3">
//           <div className="vehicle-track-stats-hero-label">{heroLabel}</div>
//           <div className="vehicle-track-stats-hero">{heroValue}</div>
//           {etaText && approxDistance ? (
//             <div className="vehicle-track-stats-note mt-1">
//               Based on {approxDistance} away at {speedText || 'current speed'} (approx.)
//             </div>
//           ) : null}
//           {isStopped && etaText ? (
//             <div className="vehicle-track-stats-note mt-1">
//               Bus stopped in traffic — arrival time held
//             </div>
//           ) : null}
//         </div>

//         <div className="row g-2 g-md-3">
//           <div className="col-4">
//             <div className="vehicle-track-stat-tile h-100">
//               <div className="vehicle-track-stat-tile-icon">
//                 <BsGeoAlt />
//               </div>
//               <div className="vehicle-track-stat-label">Approx. distance</div>
//               <div className="vehicle-track-stat-value">{distanceValue}</div>
//             </div>
//           </div>
//           <div className="col-4">
//             <div className="vehicle-track-stat-tile h-100">
//               <div className="vehicle-track-stat-tile-icon">
//                 <BsSpeedometer2 />
//               </div>
//               <div className="vehicle-track-stat-label">Bus speed</div>
//               <div
//                 className={`vehicle-track-stat-value${
//                   speedText && speedText.length > 14 ? ' vehicle-track-stat-value-sm' : ''
//                 }`}
//                 style={isStopped ? { color: '#fbbf24' } : undefined}
//               >
//                 {speedValue}
//               </div>
//             </div>
//           </div>
//           <div className="col-4">
//             <div className="vehicle-track-stat-tile h-100">
//               <div className="vehicle-track-stat-tile-icon">
//                 <BsClock />
//               </div>
//               <div className="vehicle-track-stat-label">Time to reach you</div>
//               <div className="vehicle-track-stat-value vehicle-track-stat-value-sm">
//                 {reachTimeValue}
//               </div>
//             </div>
//           </div>
//         </div>

//         {lastMs != null ? (
//           <div className="vehicle-track-stats-note mt-2">
//             Last bus update: {formatRelativeAgo(lastMs)}
//           </div>
//         ) : null}

//         {!hasUserCoords && hasCoords ? (
//           <div className="vehicle-track-location-banner mt-3">
//             {userLocationLoading ? (
//               'Getting your location to show distance and arrival time…'
//             ) : userLocationError ? (
//               <>
//                 {userLocationError}{' '}
//                 <Button
//                   variant="link"
//                   className="p-0 align-baseline"
//                   style={{ fontSize: 13, color: '#c2410c' }}
//                   onClick={onRetryLocation}
//                 >
//                   Try again
//                 </Button>
//               </>
//             ) : (
//               'Allow location access to see how far the bus is from you.'
//             )}
//           </div>
//         ) : null}

//         {hasUserCoords && hasCoords && !etaText && !userLocationLoading ? (
//           <div className="vehicle-track-stats-note mt-3">
//             Time to reach you will appear once bus speed is calculated from the next location
//             update.
//           </div>
//         ) : null}
//       </Card.Body>
//     </Card>
//   )
// }

// export default function VehicleTrackPage() {
//   const navigate = useNavigate()
//   const { vehicle_id } = useParams()
//   const { user } = useAuth()
//   const { isCollapsed } = useSidebar()
//   const [marginLeft, setMarginLeft] = useState('0px')
//   const [isMobile, setIsMobile] = useState(
//     typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
//   )

//   useEffect(() => {
//     const handleResize = () => {
//       setIsMobile(window.innerWidth <= 768)
//       if (window.innerWidth >= 992) {
//         const expandedWidth = 230
//         const collapsedWidth = 85
//         const gap = 1
//         setMarginLeft(
//           isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
//         )
//       } else {
//         setMarginLeft('0px')
//       }
//     }
//     handleResize()
//     window.addEventListener('resize', handleResize)
//     return () => window.removeEventListener('resize', handleResize)
//   }, [isCollapsed])

//   const pageStyle = {
//     marginLeft,
//     marginTop: isMobile ? '55px' : '0px',
//     transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
//     padding: 12,
//   }

//   const busId = useMemo(() => {
//     const n = Number(vehicle_id)
//     return Number.isFinite(n) && n > 0 ? n : null
//   }, [vehicle_id])

//   const [bus, setBus] = useState(null)
//   const [busLoading, setBusLoading] = useState(true)
//   const [attendantName, setAttendantName] = useState('—')
//   const [journeyActive, setJourneyActive] = useState(false)
//   const [locationLoading, setLocationLoading] = useState(false)
//   const [userCoords, setUserCoords] = useState(null)
//   const [userLocationLoading, setUserLocationLoading] = useState(false)
//   const [userLocationError, setUserLocationError] = useState(null)
//   const [busMotion, setBusMotion] = useState({
//     speedKmh: null,
//     etaSeconds: null,
//     isStopped: false,
//   })
//   const lastRefreshMsRef = useRef(0)
//   const busMotionRef = useRef({
//     prevCoords: null,
//     prevFetchedMs: null,
//     lastEtaSeconds: null,
//     lastSpeedKmh: null,
//   })

//   const { latestByBusId, fetchLatest, startOrUpdateLocation, completeJourney, inFlight } =
//     useBusJourneyTracking()

//   const canManageJourney = useMemo(() => {
//     if (!busId) return false
//     if (!isBusAttendantUser(user)) return false
//     const uid = Number(user?.id)
//     const aid = Number(bus?.bus_attendant_id)
//     return Number.isFinite(uid) && Number.isFinite(aid) && uid === aid
//   }, [busId, user, bus?.bus_attendant_id])

//   const loadBusAndAttendant = useCallback(async () => {
//     if (!busId) return
//     setBusLoading(true)
//     try {
//       const res = await ApiEndpoints.getBusById(busId)
//       const raw = res?.data?.data ?? res?.data ?? null
//       setBus(raw)
//       setJourneyActive(normalizeBusJourneyStatus(raw) === 'started')

//       const attendantId = raw?.bus_attendant_id
//       if (attendantId == null || attendantId === '') {
//         setAttendantName('—')
//         return
//       }
//       // ✅ Skip the attendants API if user is a parent (not an attendant/admin)
// if (!isBusAttendantUser(user)) {
//   setAttendantName(attendantId != null ? `User #${attendantId}` : '—')
//   return
// }
//       try {
//         const ares = await ApiEndpoints.getBusAttendants()
//         const list = ares.data?.data ?? ares.data ?? []
//         const n = Number(attendantId)
//         const row = Array.isArray(list)
//           ? list.find((x) => Number(x?.id ?? x?.user_id) === n)
//           : null
//         const label =
//           row?.name ||
//           [row?.first_name, row?.last_name].filter(Boolean).join(' ') ||
//           row?.email ||
//           `User #${attendantId}`
//         setAttendantName(label || '—')
//       } catch {
//         setAttendantName(attendantId != null ? `User #${attendantId}` : '—')
//       }
//     } catch (error) {
//       setBus(null)
//       toast.error(
//         error?.response?.data?.message || error?.message || 'Could not load vehicle details.',
//       )
//     } finally {
//       setBusLoading(false)
//     }
//   }, [busId])

//   const refreshLocation = useCallback(async () => {
//     if (!busId) return
//     setLocationLoading(true)
//     try {
//       await fetchLatest(busId)
//     } finally {
//       setLocationLoading(false)
//     }
//   }, [busId, fetchLatest])

//   useEffect(() => {
//     loadBusAndAttendant()
//   }, [loadBusAndAttendant])

//   useEffect(() => {
//     if (!busId) return
//     refreshLocation()
//   }, [busId, refreshLocation])

//   const loadUserLocation = useCallback(async () => {
//     setUserLocationLoading(true)
//     setUserLocationError(null)
//     try {
//       const position = await readGeolocationOnce()
//       setUserCoords(position)
//     } catch (error) {
//       setUserCoords(null)
//       setUserLocationError(
//         error?.code === 1
//           ? 'Location permission denied. Allow location access to see distance from you.'
//           : error?.message || 'Could not read your location.',
//       )
//     } finally {
//       setUserLocationLoading(false)
//     }
//   }, [])

//   useEffect(() => {
//     loadUserLocation()
//   }, [loadUserLocation])

//   useEffect(() => {
//     if (!busId) return
//     let cancelled = false
//     const poll = window.setInterval(() => {
//       if (!cancelled) {
//         lastRefreshMsRef.current = Date.now()
//         fetchLatest(busId)
//       }
//     }, LATEST_LOCATION_POLL_MS)
//     return () => {
//       cancelled = true
//       window.clearInterval(poll)
//     }
//   }, [busId, fetchLatest])

//   useEffect(() => {
//     busMotionRef.current = {
//       prevCoords: null,
//       prevFetchedMs: null,
//       lastEtaSeconds: null,
//       lastSpeedKmh: null,
//     }
//     setBusMotion({ speedKmh: null, etaSeconds: null, isStopped: false })
//   }, [busId])

//   const handleStartOrUpdate = useCallback(async () => {
//     if (!busId) return
//     try {
//       await startOrUpdateLocation(busId)
//       setJourneyActive(true)
//       await loadBusAndAttendant()
//     } catch {
//       /* toast in hook */
//     }
//   }, [busId, startOrUpdateLocation, loadBusAndAttendant])

//   const handleComplete = useCallback(async () => {
//     if (!busId) return
//     try {
//       await completeJourney(busId)
//       setJourneyActive(false)
//       await loadBusAndAttendant()
//     } catch {
//       /* toast in hook */
//     }
//   }, [busId, completeJourney, loadBusAndAttendant])

//   const latest = busId != null ? latestByBusId[Number(busId)] : null
//   const coords = latest?.coords ?? null
//   const hasCoords =
//     coords != null && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.long))
//   const mapCenter = hasCoords ? [Number(coords.lat), Number(coords.long)] : null
//   const lastMs = latest?.updatedMs ?? null
//   const locationText = hasCoords
//     ? `${Number(coords.lat).toFixed(6)}°, ${Number(coords.long).toFixed(6)}°`
//     : latest?.noLocationMessage || '—'
//   const hasUserCoords =
//     userCoords != null &&
//     Number.isFinite(Number(userCoords.lat)) &&
//     Number.isFinite(Number(userCoords.long))
//   const distanceKm =
//     hasCoords && hasUserCoords
//       ? haversineDistanceKm(
//           Number(coords.lat),
//           Number(coords.long),
//           Number(userCoords.lat),
//           Number(userCoords.long),
//         )
//       : null
//   const distanceText =
//     distanceKm != null ? formatDistanceKm(distanceKm) : userLocationError ? '—' : null
//   const approxDistanceText = distanceText && distanceText !== '—' ? `~${distanceText}` : null

//   useEffect(() => {
//     if (!hasCoords || latest?.lastFetchedMs == null) return

//     const current = { lat: Number(coords.lat), long: Number(coords.long) }
//     const fetchedMs = latest.lastFetchedMs
//     const motion = busMotionRef.current

//     if (motion.prevFetchedMs === fetchedMs) return

//     if (motion.prevCoords == null || motion.prevFetchedMs == null) {
//       busMotionRef.current = {
//         ...motion,
//         prevCoords: current,
//         prevFetchedMs: fetchedMs,
//       }
//       return
//     }

//     const isStopped = sameCoords(motion.prevCoords, current)
//     let speedKmh = motion.lastSpeedKmh
//     let etaSeconds = motion.lastEtaSeconds

//     if (isStopped) {
//       speedKmh = 0
//     } else {
//       const elapsedMs = fetchedMs - motion.prevFetchedMs
//       const nextSpeed = computeSpeedKmh(
//         motion.prevCoords.lat,
//         motion.prevCoords.long,
//         current.lat,
//         current.long,
//         elapsedMs,
//       )
//       if (nextSpeed != null && Number.isFinite(nextSpeed)) {
//         speedKmh = nextSpeed
//         motion.lastSpeedKmh = nextSpeed
//       }

//       if (distanceKm != null && speedKmh != null && speedKmh > 0) {
//         etaSeconds = computeEtaSeconds(distanceKm, speedKmh)
//         motion.lastEtaSeconds = etaSeconds
//       }
//     }

//     busMotionRef.current = {
//       ...motion,
//       prevCoords: current,
//       prevFetchedMs: fetchedMs,
//       lastEtaSeconds: etaSeconds,
//       lastSpeedKmh: isStopped ? motion.lastSpeedKmh : speedKmh,
//     }

//     setBusMotion({
//       speedKmh: speedKmh ?? null,
//       etaSeconds: etaSeconds ?? null,
//       isStopped,
//     })
//   }, [coords?.lat, coords?.long, distanceKm, hasCoords, latest?.lastFetchedMs])

//   const speedText =
//     busMotion.speedKmh != null
//       ? busMotion.isStopped
//         ? 'Stopped (traffic)'
//         : formatSpeedKmh(busMotion.speedKmh)
//       : null

//   const etaSeconds = useMemo(() => {
//     if (distanceKm == null || !hasUserCoords) return null
//     if (busMotion.isStopped) return busMotion.etaSeconds
//     const speedForEta = busMotion.isStopped ? null : busMotion.speedKmh
//     if (speedForEta != null && speedForEta > 0) {
//       return computeEtaSeconds(distanceKm, speedForEta)
//     }
//     return busMotion.etaSeconds
//   }, [
//     busMotion.etaSeconds,
//     busMotion.isStopped,
//     busMotion.speedKmh,
//     distanceKm,
//     hasUserCoords,
//   ])

//   const etaText =
//     etaSeconds != null ? `${formatEtaSeconds(etaSeconds)} (approx.)` : null

//   const mapPoints = []
//   if (hasCoords) mapPoints.push([Number(coords.lat), Number(coords.long)])
//   if (hasUserCoords) mapPoints.push([Number(userCoords.lat), Number(userCoords.long)])
//   const busNumber = bus?.bus_number ?? bus?.busNumber ?? busId

//   if (busId == null) {
//     return (
//       <div style={pageStyle}>
//       <Container className="py-4">
//         <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
//           <Card.Body className="p-4">
//             <div className="d-flex align-items-center justify-content-between gap-3">
//               <div>
//                 <div className="fw-bold" style={{ fontSize: 16 }}>
//                   Invalid vehicle id
//                 </div>
//                 <div className="text-muted" style={{ fontSize: 13 }}>
//                   Please open this page with a valid vehicle id.
//                 </div>
//               </div>
//               <Button variant="outline-secondary" onClick={() => navigate(-1)}>
//                 Go back
//               </Button>
//             </div>
//           </Card.Body>
//         </Card>
//       </Container>
//       </div>
//     )
//   }

//   return (
//     <div style={pageStyle}>
//       <Container fluid className="p-0">
//         <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
//           <Button
//             variant="outline-secondary"
//             className="d-inline-flex align-items-center gap-2"
//             onClick={() => navigate(-1)}
//           >
//             <BsArrowLeft />
//             Back
//           </Button>
//           <Button
//             variant="outline-primary"
//             className="d-inline-flex align-items-center gap-2"
//             disabled={busLoading}
//             onClick={() => {
//               loadBusAndAttendant()
//               refreshLocation()
//             }}
//           >
//             <BsArrowClockwise />
//             Refresh
//           </Button>
//         </div>

//         {busLoading ? (
//           <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
//             <Card.Body className="p-4 d-flex align-items-center gap-2">
//               <Spinner animation="border" size="sm" />
//               <span className="text-muted">Loading vehicle…</span>
//             </Card.Body>
//           </Card>
//         ) : (
//           <>
//             <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
//               <Card.Body className="p-3 p-md-4">
//                 <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
//                   <div className="min-w-0">
//                     <div className="fw-bold" style={{ fontSize: 16, color: '#0f172a' }}>
//                       {dash(busNumber)}
//                     </div>
//                     <div className="text-muted" style={{ fontSize: 13 }}>
//                       Attendant: {dash(attendantName)}
//                     </div>
//                   </div>
//                   <div className="d-flex align-items-center gap-2">
//                     <Badge bg={journeyActive ? 'success' : hasCoords ? 'info' : 'secondary'} pill>
//                       {journeyActive ? 'Journey live' : hasCoords ? 'Last known' : 'No position'}
//                     </Badge>
//                   </div>
//                 </div>
//               </Card.Body>
//             </Card>

//             <style>{vehicleTrackMapCss}</style>
//             <TrackingStatsPanel
//               distanceText={distanceText}
//               speedText={speedText}
//               etaText={etaText}
//               isStopped={busMotion.isStopped}
//               lastMs={lastMs}
//               locationLoading={locationLoading}
//               userLocationLoading={userLocationLoading}
//               userLocationError={userLocationError}
//               hasUserCoords={hasUserCoords}
//               hasCoords={hasCoords}
//               onRetryLocation={loadUserLocation}
//             />

//             <Card
//               className="border-0 shadow-sm mb-3 vehicle-track-map-card"
//               style={{ borderRadius: 12 }}
//             >
//               <div
//                 className="vehicle-track-map-inner"
//                 style={{
//                   height: 340,
//                   background: '#e2e8f0',
//                   borderRadius: 12,
//                   overflow: 'hidden',
//                 }}
//               >
//                 {mapCenter ? (
//                   <MapContainer
//                     center={mapCenter}
//                     zoom={16}
//                     scrollWheelZoom={false}
//                     style={{ height: '100%', width: '100%' }}
//                   >
//                     <TileLayer
//                       attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
//                       url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//                     />
//                     {mapPoints.length >= 2 ? (
//                       <FitMapBounds points={mapPoints} fallbackCenter={mapCenter} fallbackZoom={16} />
//                     ) : (
//                       <RecenterMap center={mapCenter} zoom={16} />
//                     )}
//                     <Marker position={mapCenter}>
//                       <Popup maxWidth={220} closeButton={false}>
//                         <div style={{ maxWidth: 220 }}>
//                           <div className="fw-semibold" style={{ fontSize: 12 }}>
//                             {dash(busNumber)}
//                           </div>
//                           <div style={{ fontSize: 11, color: '#64748b' }}>{locationText}</div>
//                         </div>
//                       </Popup>
//                     </Marker>
//                     {hasUserCoords ? (
//                       <Marker
//                         position={[Number(userCoords.lat), Number(userCoords.long)]}
//                         icon={userLocationIcon}
//                       >
//                         <Popup maxWidth={220} closeButton={false}>
//                           <div style={{ maxWidth: 220 }}>
//                             <div className="fw-semibold" style={{ fontSize: 12 }}>
//                               Your location
//                             </div>
//                             {approxDistanceText ? (
//                               <div style={{ fontSize: 11, color: '#64748b' }}>
//                                 {approxDistanceText} from bus (approx.)
//                               </div>
//                             ) : null}
//                           </div>
//                         </Popup>
//                       </Marker>
//                     ) : null}
//                   </MapContainer>
//                 ) : (
//                   <div className="h-100 w-100 d-flex align-items-center justify-content-center">
//                     <div className="text-muted small">{latest?.noLocationMessage || 'No position yet'}</div>
//                   </div>
//                 )}
//               </div>
//             </Card>

//             <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
//               <Card.Body className="p-3 p-md-4">
//                 <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
//                   <div>
//                     <div className="text-muted" style={{ fontSize: 12 }}>
//                       Bus coordinates
//                     </div>
//                     <div className="fw-semibold" style={{ fontSize: 14, color: '#0f172a' }}>
//                       {locationLoading ? 'Loading…' : locationText}
//                     </div>
//                     {lastMs != null ? (
//                       <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
//                         {new Date(lastMs).toLocaleString()}
//                       </div>
//                     ) : null}
//                     {latest?.error ? (
//                       <div className="text-danger" style={{ fontSize: 12, marginTop: 6 }}>
//                         {latest.error}
//                       </div>
//                     ) : null}
//                   </div>

//                   <div className="d-flex flex-wrap gap-2 justify-content-end">
//                     <Button
//                       variant="outline-primary"
//                       className="d-inline-flex align-items-center gap-2"
//                       disabled={!busId || Boolean(inFlight?.[Number(busId)])}
//                       onClick={() => refreshLocation()}
//                     >
//                       <BsArrowClockwise />
//                       Refresh
//                     </Button>

//                     {canManageJourney ? (
//                       <>
//                         <Button
//                           variant="success"
//                           className="d-inline-flex align-items-center gap-2 fw-semibold"
//                           disabled={!busId || inFlight?.[Number(busId)] === 'start'}
//                           onClick={handleStartOrUpdate}
//                         >
//                           <FaMapMarkerAlt />
//                           {journeyActive ? 'Send current location' : 'Start journey (GPS)'}
//                         </Button>
//                         <Button
//                           variant="danger"
//                           className="fw-semibold"
//                           disabled={
//                             !busId || !journeyActive || inFlight?.[Number(busId)] === 'complete'
//                           }
//                           onClick={handleComplete}
//                         >
//                           Complete journey
//                         </Button>
//                       </>
//                     ) : null}
//                   </div>
//                 </div>
//               </Card.Body>
//             </Card>

//             <div className="text-muted" style={{ fontSize: 12, paddingLeft: 2 }}>
//               Auto-refreshing latest location every 30 seconds.
//             </div>
//           </>
//         )}
//       </Container>
//     </div>
//   )
// }

